// ─────────────────────────────────────────────────────────────
// offlineCatalog — cache hors-ligne du catalogue (produits, catégories).
//
// Permet d'ouvrir la caisse, la gestion de stock et la liste des produits
// MÊME SANS INTERNET : le dernier catalogue connu est conservé localement
// (IndexedDB) et resservi quand le réseau est indisponible. Les données en
// ligne écrasent le cache dès qu'elles sont rechargées.
// ─────────────────────────────────────────────────────────────

const DB_NAME = 'zifek-offline-catalog';
const STORE = 'parts';
const DB_VERSION = 1;

export type CatalogPart = 'products' | 'categories';

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Enregistre la dernière version connue d'une partie du catalogue. */
export async function saveCatalogPart(
  commerceId: number | string,
  part: CatalogPart,
  rows: unknown[],
): Promise<void> {
  if (!hasIndexedDb() || !commerceId) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ key: `${commerceId}:${part}`, rows, savedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* best-effort */
  }
}

/** Relit la dernière version connue d'une partie du catalogue. */
export async function readCatalogPart<T = unknown>(
  commerceId: number | string,
  part: CatalogPart,
): Promise<T[] | null> {
  if (!hasIndexedDb() || !commerceId) return null;
  try {
    const db = await openDb();
    const record = await new Promise<{ rows?: T[] } | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(`${commerceId}:${part}`);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return record?.rows ?? null;
  } catch {
    return null;
  }
}